/**
 * LiveDashboard - Real-time streaming statistics and health monitoring
 */

import React, { useState, useEffect } from 'react';
import { StreamStats, Platform, formatDuration, formatViewerCount } from '../../lib/golive';

interface LiveDashboardProps {
  stats: StreamStats | null;
  platforms: Platform[];
  startTime: Date | null;
  isRecording: boolean;
  onStopStream: () => void;
  onToggleRecording: () => void;
}

export const LiveDashboard: React.FC<LiveDashboardProps> = ({
  stats,
  platforms,
  startTime,
  isRecording,
  onStopStream,
  onToggleRecording,
}) => {
  const [elapsed, setElapsed] = useState(0);
  const [confirmStop, setConfirmStop] = useState(false);

  // Update elapsed time every second
  useEffect(() => {
    if (!startTime) return;

    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime.getTime()) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const enabledPlatforms = platforms.filter((p) => p.enabled);
  const livePlatforms = enabledPlatforms.filter((p) => p.status === 'live');
  const totalViewers = enabledPlatforms.reduce((sum, p) => sum + (p.viewerCount || 0), 0);

  const getHealthColor = () => {
    if (!stats) return '#6b7280';
    if (stats.bitrate < 1000) return '#ef4444'; // Red - low bitrate
    if (stats.bitrate < 2500) return '#f59e0b'; // Yellow - warning
    return '#22c55e'; // Green - healthy
  };

  const getHealthText = () => {
    if (!stats) return 'Connecting...';
    if (stats.bitrate < 1000) return 'Poor';
    if (stats.bitrate < 2500) return 'Fair';
    return 'Excellent';
  };

  return (
    <div className="live-dashboard">
      {/* Live Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          borderRadius: '12px',
          marginBottom: '20px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: 'white',
              animation: 'pulse 1s infinite',
            }}
          />
          <span style={{ color: 'white', fontSize: '20px', fontWeight: 700 }}>LIVE</span>
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '24px', fontWeight: 600, fontFamily: 'monospace' }}>
            {formatDuration(elapsed)}
          </span>
        </div>

        <button
          onClick={() => setConfirmStop(true)}
          style={{
            padding: '10px 24px',
            background: 'rgba(255,255,255,0.2)',
            border: '2px solid white',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          <i className="fas fa-stop" style={{ marginRight: '8px' }} />
          End Stream
        </button>
      </div>

      {/* Stats Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '16px',
          marginBottom: '20px',
        }}
      >
        {/* Total Viewers */}
        <div
          style={{
            padding: '20px',
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '32px', fontWeight: 700, color: 'var(--primary)' }}>
            {formatViewerCount(totalViewers)}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Total Viewers
          </div>
        </div>

        {/* Platforms Live */}
        <div
          style={{
            padding: '20px',
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#22c55e' }}>
            {livePlatforms.length}/{enabledPlatforms.length}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Platforms Live
          </div>
        </div>

        {/* Bitrate */}
        <div
          style={{
            padding: '20px',
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '32px', fontWeight: 700, color: 'var(--text-primary)' }}>
            {stats ? `${Math.round(stats.bitrate / 1000)}` : '--'}
            <span style={{ fontSize: '14px', fontWeight: 400, marginLeft: '4px' }}>Mbps</span>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Bitrate
          </div>
        </div>

        {/* Stream Health */}
        <div
          style={{
            padding: '20px',
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: getHealthColor(),
              }}
            />
            <span style={{ fontSize: '20px', fontWeight: 600, color: getHealthColor() }}>
              {getHealthText()}
            </span>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>
            Stream Health
          </div>
        </div>
      </div>

      {/* Platform Status List */}
      <div
        style={{
          background: 'var(--bg-secondary)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
        }}
      >
        <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>
          Platform Status
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {enabledPlatforms.map((platform) => (
            <div
              key={platform.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                background: 'var(--bg-tertiary)',
                borderRadius: '8px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <i
                  className={platform.icon}
                  style={{
                    fontSize: '20px',
                    color: platform.status === 'live' ? '#22c55e' : 'var(--text-secondary)',
                  }}
                />
                <span style={{ fontWeight: 500 }}>{platform.name}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                  {formatViewerCount(platform.viewerCount || 0)} viewers
                </span>
                <span
                  style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    background:
                      platform.status === 'live'
                        ? 'rgba(34, 197, 94, 0.1)'
                        : platform.status === 'error'
                        ? 'rgba(239, 68, 68, 0.1)'
                        : 'rgba(107, 114, 128, 0.1)',
                    color:
                      platform.status === 'live'
                        ? '#22c55e'
                        : platform.status === 'error'
                        ? '#ef4444'
                        : '#6b7280',
                  }}
                >
                  {platform.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recording Toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          background: isRecording ? 'rgba(239, 68, 68, 0.1)' : 'var(--bg-secondary)',
          borderRadius: '12px',
          border: isRecording ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid var(--border-color)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <i
            className="fas fa-circle"
            style={{
              color: isRecording ? '#ef4444' : 'var(--text-secondary)',
              animation: isRecording ? 'pulse 1s infinite' : 'none',
            }}
          />
          <div>
            <div style={{ fontWeight: 600 }}>Recording</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {isRecording ? 'Stream is being recorded' : 'Click to start recording'}
            </div>
          </div>
        </div>
        <button
          onClick={onToggleRecording}
          style={{
            padding: '10px 20px',
            background: isRecording ? '#ef4444' : 'var(--primary)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          {isRecording ? 'Stop Recording' : 'Start Recording'}
        </button>
      </div>

      {/* Confirm Stop Modal */}
      {confirmStop && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setConfirmStop(false)}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '24px',
              width: '100%',
              maxWidth: '400px',
              margin: '16px',
              textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(239, 68, 68, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '28px', color: '#ef4444' }} />
            </div>

            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px' }}>End Livestream?</h3>
            <p style={{ margin: '0 0 24px 0', color: 'var(--text-secondary)' }}>
              This will disconnect from all {enabledPlatforms.length} platform(s) and end your stream.
              {isRecording && ' Your recording will be saved.'}
            </p>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setConfirmStop(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                }}
              >
                Keep Streaming
              </button>
              <button
                onClick={() => {
                  setConfirmStop(false);
                  onStopStream();
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#ef4444',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                End Stream
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export default LiveDashboard;
